// Shared display helpers for schema-validation finding rows.
//
// Two consumers today:
//   - src/scenes/execute/components/SchemaValidationDetailsCard.jsx
//     (Federated Data Review card on /execute, only shown for warn/fail)
//   - src/scenes/configs/components/SchemaPreCheckPanel.jsx
//     (Schema Pre-check panel on /configs)
//
// Kept here so the two surfaces never drift — adding a new finding code
// only needs one entry, and friendly reframes apply everywhere.
//
// dev-0.7.x baselines covered: 0.7.0 → 0.7.10.

// ─── Per-code tooltip text (mono badge hover) ───────────────────────────
//
// Mock validator does not emit `code`. Unknown codes still render with a
// generic "Finding code: <code>" tooltip via the badge fallback below.
//
// Blocking vs non-blocking is driven by the response's `blocked` flag.
// Tooltips below explicitly tag blocking codes (l2_data for tabular data
// fails, l2_contract for imaging schema-contract fails).
export const CODE_TOOLTIPS = {
  SCHEMA_FILE_IS_MOCK:
    "DEV schema is a mock, expected during the current Schema v1 contract phase.",
  READINESS_WARMING_UP:
    "Site capability is currently warming up. Validation may still proceed.",
  DATA_FILE_NOT_READABLE:
    "Backend reached the site-local data inspection step but cannot read the data file in this DEV runtime. Non-blocking, detailed feature, label, and sample-level findings will surface once site-local data access lands.",
  // dev-0.7.29: backend stopped emitting these two codes for imaging
  // (file-level validation is no longer unconditionally deferred; the
  // ProstateX cls 3-layer channels declaration was aligned). Kept as
  // defense-in-depth so an unexpected re-emergence still tooltips cleanly,
  // but they should never fire in practice on dev-0.7.29+ deploys.
  IMAGING_FILE_VALIDATION_DEFERRED:
    "Imaging file-level validation is deferred, image / mask / PVC inspection not yet enforced in this DEV runtime. (dev-0.7.29: backend stopped emitting; entry kept as defense-in-depth.)",
  IMAGING_VALIDATION_DEFERRED:
    "Imaging validation is deferred, full image / mask / PVC validation not yet enforced. (Older code name; canonical dev-0.7.2+ code is IMAGING_FILE_VALIDATION_DEFERRED.)",
  CHANNELS_REGISTRY_RUNTIME_DRIFT:
    "Channel metadata in the registry differs from runtime observation. Non-blocking in this DEV phase. (dev-0.7.29: ProstateX cls 3-layer channels declaration aligned to 2, should not fire on dev-0.7.29+.)",
  EXTRA_COLUMNS_IGNORED:
    "Site-local data contains columns not in the schema; they are ignored during training. Non-blocking, review the message for the column names if listed.",
  MISSING_LABEL_CLASS:
    "Site-local data has fewer label classes than the schema declares. Non-blocking in DEV, training can still proceed; aggregation will see no samples for the missing class at this site.",
  READINESS_NEEDS_ATTENTION:
    "Site capability is flagged for review. Non-blocking by itself, pair with any other fail-severity finding in the same row to understand the full picture.",
  MISSING_REQUIRED_FEATURE:
    "Site-local data is missing a required feature column declared by the schema. Blocks training (blocked=true, abortStage=l2_data), the message names the specific missing column.",
  LABEL_OUT_OF_RANGE:
    "Site-local data contains label values outside the declared label_space. Blocks training (blocked=true, abortStage=l2_data), fix the data labels or the schema's label_space before re-running.",
  MASK_ENCODING_INVALID:
    "The imaging schema's mask_encoding declaration is not one of the allowed values. Blocks training (blocked=true, abortStage=l2_contract), the message names the bad value and the allowed list.",
  MASK_SHAPE_INVALID:
    "The imaging schema's mask_shape is inconsistent with its mask_encoding. Blocks training (blocked=true, abortStage=l2_contract), the message names the specific dimension mismatch.",
  // ─── dev-0.7.29 — new codes ─────────────────────────────────────────
  SCHEMA_FILE_INVALID:
    "The schema file backend resolved for this capability has at least one invalid field (e.g. malformed label_mapping). Blocks training, the message names the specific field. Used in dev demos to gate caps whose Stage 2 data has known anomalies.",
  MASK_PAIRING_INVALID:
    "The imaging schema's image_mask_pairing.kind value is not one of the allowed strategies. Blocks training (blocked=true, abortStage=l2_contract), the message names the bad value.",
  TOO_MANY_LOGICAL_SITES_FOR_DEV:
    "AKS DEV / demo deployment supports at most 2 physical execution slots per run. Reduce the site selection to 2 and re-validate. Hard cap, every site row in the response will carry this finding when over budget.",
};

// ─── Per-code title / message reframe ───────────────────────────────────
//
// Optional UI-side rewrite for codes whose backend `title` / `message` are
// technically accurate but easy to misread. Each entry replaces what the
// finding row shows; the backend's original string is dropped from view
// (still on the wire / in DevTools). Use sparingly — only for known
// DEV-phase signals where backend wording risks misleading users.
export const CODE_FRIENDLY_OVERRIDES = {
  // dev-0.7.3+: backend labels every DEV schema as a mock. With dev-0.7.3
  // the data-vs-schema layer is real (real n_samples, real label_hist,
  // real missing-feature detection). The user should not read "DEV/mock"
  // as "the whole result is fake".
  SCHEMA_FILE_IS_MOCK: {
    title: "DEV schema contract",
    message:
      "This check used a development Schema v1 contract. The data-level validation result still applies, but the schema contract is not yet marked as authoritative.",
  },
};

// ─── Resolved display fields for one finding ────────────────────────────
//
// Returns { title, message, tooltip } — title/message respect
// CODE_FRIENDLY_OVERRIDES, falling back to backend's original. tooltip
// is the hover text for the mono code badge, falling back to a generic
// "Finding code: <code>" for unknown codes.
export const getFindingDisplay = (finding) => {
  if (!finding || typeof finding !== "object") {
    return { title: "", message: "", tooltip: "" };
  }
  const code = finding.code;
  const friendly = code ? CODE_FRIENDLY_OVERRIDES[code] : null;
  return {
    title: friendly?.title || finding.title || "",
    message: friendly?.message || finding.message || "",
    tooltip: code
      ? CODE_TOOLTIPS[code] || `Finding code: ${code}`
      : "",
  };
};

// ─── Stable fail-first sort ─────────────────────────────────────────────
//
// Sorts findings so blocking (fail) entries appear first, then warn,
// then pass. Within a severity, preserves backend ordering. Safe on
// undefined / null severities (treated as last).
const SEVERITY_RANK = { fail: 0, warn: 1, pass: 2 };
export const sortFindingsBySeverity = (findings) => {
  if (!Array.isArray(findings)) return [];
  return [...findings]
    .map((f, i) => ({ f, i }))
    .sort((a, b) => {
      const sa = SEVERITY_RANK[a.f?.severity] ?? 9;
      const sb = SEVERITY_RANK[b.f?.severity] ?? 9;
      return sa === sb ? a.i - b.i : sa - sb;
    })
    .map(({ f }) => f);
};
